import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import Icon from "../../components/Icon/Icon";
import StripeTrustButtonInner from "../../components/StripeTrustButtonInner/StripeTrustButtonInner";
import "./ViewCreation.scss";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
    addToLibrary,
    deleteFromLibrary,
    fetchCreationById,
    fetchDeleteCreation,
    fetchLikeCreation,
    fetchSaveUnSaveCreation,
    fetchSubscribe,
    fetchUnLikeCreation,
    likeUnlikeCreation,
    reloadCreation,
    saveUnSaveCreation,
    setViewCreation,
} from "../../Reducers/actions/creationsActions";
import ProfilePicture from "../../components/ProfilePicture/ProfilePicture";
import CommentSection from "../../components/CommentSection/CommentSection";
import ReportCreationSheet from "../../components/ReportCreationSheet/ReportCreationSheet";
import MediaPlayer from "../../components/MediaPlayer/MediaPlayer";
import TrackList from "./TrackList";
import MediaCarousel from "../../components/MediaCarousel/MediaCarousel";
import { capitalizeFirstLetter, htmlToPlainTextServer } from "../../../helpers/utilities";
import { getUserDisplayName } from "../../../helpers/userDisplayName";
import ContributionSection from "../../components/ContributionSection/ContributionSection";
import ContributionSuccess from "../../components/ContributionSection/ContributionSuccess";

import { loadStripe } from "@stripe/stripe-js";
import { createPaymentSession } from "../../Reducers/actions/paymentActions";
import { PAYMENT_TYPES, HYK_FEE_PERCENT, STRIPE_FEE_PERCENT, STRIPE_FIXED_FEE, VAT_PERCENT } from "../../constants/globals";
import { calculateFullPrice, fromCentsToPounds } from "../../../helpers/money";
import { createOrderForFreeCreationOnly } from "../../Reducers/actions/ordersActions";
import EditCreation from "../../components/EditCreation/EditCreation";
import DeleteCreation from "../../components/DeleteCreation/DeleteCreation";
import ModifyCreation from "../../components/ModifyCreation/ModifyCreation";
import EditMediaPreview from "../../components/EditMediaPreview/EditMediaPreview";
import { handleGoBackRedirection } from "../../components/Layout/Layout";
import SEOHead from "../../components/SEO/SEOHead";
import { GENRE_ICONS } from "../../constants/genres";
import AboutCreator from "../../components/AboutCreator/AboutCreator";
import BioPreview from "../../components/BioPreview/BioPreview";
import Unsubscribe from "../../components/Unsubscribe/Unsubscribe";
import PriceDetails from "../../components/PriceDetails/PriceDetails";
import Contribute from "../../components/Contribute/Contribute";
import ModalMessage from "../../components/ModalMessage/ModalMessage";
import NotFound from "../NotFound/NotFound";
import MetaTags from "../../components/MetaTags/MetaTags";
import { getOGImageUrl } from "../../../helpers/getOGImageUrl";
import { getProfilePublicPath } from "../../../helpers/profilePaths";
import { useAutoContributePrompt } from "../../hooks/useAutoContributePrompt";
import ViewCreationSkeleton from "./ViewCreationSkeleton";
import CircularProgress from "../../components/CircularProgress/CircularProgress";
import { useSoftwall } from "../SoftWall/SoftWall";
import ModalPurchaseAfterPreview from "../../components/ModalPurchaseAfterPreview/ModalPurchaseAfterPreview";
import ShippingAddressSelector from "../../components/ShippingAddressSelector/ShippingAddressSelector";
import { getShippingAddresses } from "../../Reducers/actions/shippingAddressActions";
import { getShippingMethodById } from "../../Reducers/actions/shippingMethodsActions";
import { MIME_TO_EXT, getExtensionFromMime, isSupportedExtension } from "../../../helpers/fileConstants";
import { useToast } from "../../hooks/useToast";
import { getPreferredStreamMimeType, getPreferredStreamUrl } from "../../../helpers/mediaPlayback";
import { savePendingContributionIntent } from "../../constants/signupFlow";
import { isOwnerProfileView } from "./ownerProfile";

const DEFAULT_MAX_UNLIMITED = 99;

/**
 * Get file extension from URL or content type with comprehensive validation
 * @param {string} url - File URL
 * @param {string} contentType - Content-Type header value (optional)
 * @returns {string} Valid file extension without dot, or 'file' as fallback
 */
const getFileExtension = (url, contentType = null) => {
    // Try to extract extension from URL first
    if (url) {
        // Remove query parameters and hash
        const urlWithoutParams = url.split('?')[0].split('#')[0];
        const urlExtension = urlWithoutParams.split('.').pop()?.toLowerCase() || '';

        // Validate extension: must be 1-5 characters and supported
        if (urlExtension && urlExtension.length > 0 && urlExtension.length <= 5) {
            if (isSupportedExtension(urlExtension)) {
                return urlExtension;
            }
        }
    }

    // Fall back to content type if URL extension is invalid or missing
    if (contentType) {
        // Handle content-type with charset or other parameters (e.g., "image/jpeg; charset=utf-8")
        const mimeType = contentType.split(';')[0].trim();
        const extension = getExtensionFromMime(mimeType);
        if (extension) {
            return extension;
        }
    }

    // Final fallback
    return 'file';
};

const ViewCreation = ({
    creation: init_creation,
    preview = false,
    handleSubmit,
}) => {
    const { creations, user_id, auth_user_id, creation, reload_creation, loading_creation, error_creation } = useSelector((state) => ({
        creations: state.creation.creations,
        creation: state.creation.creation,
        user_id: state.auth?.session?.user?.id,
        auth_user_id: state.auth?.user?.user_id,
        reload_creation: state.creation.reload_creation,
        loading_creation: state.creation.loading,
        error_creation: state.creation.error,
    }));
    const sw = useSoftwall();
    const showToast = useToast();
    const [searchParams] = useSearchParams();
    const { creation_id: paramCreationId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [openCreationMenu, setOpenCreationMenu] = useState(false)
    const [openDeleteCreation, setOpenDeleteCreation] = useState(false)
    // const [openModifyCreation, setOpenModifyCreation] = useState(false)
    const [openEditMedia, setOpenEditMedia] = useState(false)
    const fromProfile = isOwnerProfileView({
        initCreation: init_creation,
        creation,
        authUserId: auth_user_id,
        sessionUserId: user_id,
    });
    const [error, setError] = useState(null);
    const [showComments, setShowComments] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [disablePurchaseButton, setDisablePurchaseButton] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [showContribution, setShowContribution] = useState(false);
    const autoContributePrompt = useAutoContributePrompt(auth_user_id, creation?.users?.user_id, showContribution);
    const [showContribute, setShowContribute] = useState(false);
    const [showSuccessContribution, setShowSuccessContribution] = useState(
        searchParams.get("success") === "true"
    );
    const [toast, setToast] = useState(false);

    // const [reload, setReload] = useState(reload_creation);
    const [loading, setLoading] = useState(false);
    const [openMenu, setOpenMenu] = useState(false);
    const [showFullBio, setShowFullBio] = useState(false)
    const [showUnsubscribe, setShowUnsubscribe] = useState(false)
    const [showPriceDetails, setShowPriceDetails] = useState(false)
    const [openNotFree, setOpenNotFree] = useState(false)
    const [openRemove, setOpenRemove] = useState(false)
    const [creditsExpanded, setCreditsExpanded] = useState(false);

    const [openPurchaseAfterPreview, setOpenPurchaseAfterPreview] = useState(false)
    const [openDownloadPurchaseModal, setOpenDownloadPurchaseModal] = useState(false)
    const [showShippingAddressSelector, setShowShippingAddressSelector] = useState(false)
    const [selectedShippingAddress, setSelectedShippingAddress] = useState(null)
    const [isDownloading, setIsDownloading] = useState(false)
    const [downloadProgress, setDownloadProgress] = useState(0)
    const [shippingAvailabilityError, setShippingAvailabilityError] = useState(null)
    const [creationShippingMethod, setCreationShippingMethod] = useState(null)
    const [loadingShippingMethod, setLoadingShippingMethod] = useState(false)
    const [shippingMethodFetchError, setShippingMethodFetchError] = useState(false)

    const { addresses } = useSelector((state) => state.shippingAddress || { addresses: [] });

    const rawMedia = (creation || {}).media;
    const mediaArray = Array.isArray(rawMedia) ? rawMedia : rawMedia ? [rawMedia] : [];
    const media = mediaArray[0] ?? null;
    const isMultiTrackMusic = (creation?.category || '').toLowerCase() === 'music' && mediaArray.length > 1;
    const isPhysical = mediaArray.length > 0
        ? mediaArray[0]?.media_format === 'physical'
        : false;
    const isDigital = !isPhysical;
    const preferredStreamUrl = useMemo(() => getPreferredStreamUrl(media), [media]);
    const preferredStreamMimeType = useMemo(() => getPreferredStreamMimeType(media), [media]);
    const isOrderedBefore = (creation?.orders || []).some(
        (order) => order.customer_id === user_id
    );

    const creationSizes = useMemo(() => {
        const sizes = creation?.creation_sizes || [];
        console.log('[ViewCreation] creationSizes useMemo:', {
            hasCreation: !!creation,
            rawCreationSizes: creation?.creation_sizes,
            processedSizes: sizes,
            sizesLength: sizes.length
        });
        return sizes;
    }, [creation?.creation_sizes]);
    const [sizeSelectionError, setSizeSelectionError] = useState(null);

    // Get initial playback time from URL params (for continuing playback from preview)
    const initialTime = useMemo(() => {
        const startTime = searchParams.get('startTime');
        return startTime ? parseFloat(startTime) : null;
    }, [searchParams]);

    // HYK-914: multi-track music playback state
    const [selectedTrackIndex, setSelectedTrackIndex] = useState(0);
    const [shouldAutoplaySelectedTrack, setShouldAutoplaySelectedTrack] = useState(false);

    useEffect(() => {
        // Reset when switching creations
        setSelectedTrackIndex(0);
        setShouldAutoplaySelectedTrack(false);
    }, [creation?.creation_id]);

    const goToNextTrack = useCallback(() => {
        if (!isMultiTrackMusic) return;
        setShouldAutoplaySelectedTrack(true);
        setSelectedTrackIndex((idx) => {
            const next = idx + 1;
            return next >= mediaArray.length ? 0 : next;
        });
    }, [isMultiTrackMusic, mediaArray.length]);

    const handlePlaybackEnded = useCallback(() => {
        if (!isMultiTrackMusic) return;
        goToNextTrack();
    }, [isMultiTrackMusic, goToNextTrack]);

    const selectedTrack = isMultiTrackMusic ? (mediaArray[selectedTrackIndex] || mediaArray[0]) : media;
    const selectedTrackStreamUrl = useMemo(() => getPreferredStreamUrl(selectedTrack), [selectedTrack]);
    const selectedTrackStreamMimeType = useMemo(() => getPreferredStreamMimeType(selectedTrack), [selectedTrack]);

    const requiresSizeSelection = useMemo(() => {
        const category = creation?.category;
        return Boolean(category && category.toLowerCase() === 'clothing' && creationSizes.length > 0);
    }, [creation?.category, creationSizes.length]);

    const getSizeMeta = useCallback((label) => {
        if (!label) return undefined;
        return creationSizes.find((size) => size?.size_label === label);
    }, [creationSizes]);

    const getQuantityBounds = useCallback((label) => {
        const size = getSizeMeta(label);
        let rawMax = size?.stock_quantity;
        if (typeof rawMax === "string" && rawMax !== "") {
            const parsed = Number(rawMax);
            rawMax = Number.isNaN(parsed) ? undefined : parsed;
        }
        const max = (typeof rawMax === "number" && !Number.isNaN(rawMax))
            ? Math.max(0, rawMax)
            : DEFAULT_MAX_UNLIMITED;
        const min = max === 0 ? 0 : 1;
        return { min, max };
    }, [getSizeMeta]);

    const getStockInfo = useCallback((label) => {
        const size = getSizeMeta(label);
        let quantity = size?.stock_quantity;
        if (typeof quantity === "string" && quantity !== "") {
            const parsed = Number(quantity);
            quantity = Number.isNaN(parsed) ? null : parsed;
        }

        const isUnlimited = quantity === null || quantity === undefined;
        const normalizedQuantity = isUnlimited ? null : Math.max(0, quantity);
        const isOutOfStock = !isUnlimited && normalizedQuantity === 0;
        const labelText = isUnlimited
            ? "Unlimited stock"
            : isOutOfStock
                ? "Out of stock"
                : `${normalizedQuantity} in stock`;
        return {
            quantity: normalizedQuantity,
            isUnlimited,
            isOutOfStock,
            label: labelText,
        };
    }, [getSizeMeta]);

    const getSizeIdentifier = useCallback((label) => {
        const size = getSizeMeta(label);
        return size?.size_id ?? size?.id ?? null;
    }, [getSizeMeta]);

    const [sizeSelections, setSizeSelections] = useState([]);
    const validSizeSelections = useMemo(() => sizeSelections.filter((selection) => selection?.size_label && typeof selection.quantity === 'number' && selection.quantity > 0), [sizeSelections]);

    useEffect(() => {
        const handleResumeContribution = (event) => {
            const pendingIntent = event?.detail;
            if (pendingIntent?.source !== 'contribute') return;
            const intentCreatorId = pendingIntent?.creation?.creator_id;
            const currentCreatorId = creation?.creator_id || creation?.users?.user_id;
            if (
                intentCreatorId &&
                currentCreatorId &&
                String(intentCreatorId) !== String(currentCreatorId)
            ) {
                return;
            }
            setShowContribution(true);
        };
        window.addEventListener('hyk:resume-contribution', handleResumeContribution);
        return () => window.removeEventListener('hyk:resume-contribution', handleResumeContribution);
    }, [creation?.creator_id, creation?.users?.user_id]);

    useEffect(() => {
        console.log('[ViewCreation] sizeSelections useEffect triggered:', {
            creationSizesLength: creationSizes.length,
            creationSizes: creationSizes,
            currentSizeSelections: sizeSelections
        });

        if (!creationSizes.length) {
            console.log('[ViewCreation] No creationSizes, clearing sizeSelections');
            setSizeSelections([]);
            setSizeSelectionError(null);
            return;
        }

        setSizeSelections((prev) => {
            console.log('[ViewCreation] Setting sizeSelections, prev:', prev);
            const filtered = prev
                .map((selection) => {
                    const exists = creationSizes.some((size) => size?.size_label === selection?.size_label);
                    if (!exists) return null;
                    const { min, max } = getQuantityBounds(selection.size_label);
                    const current = typeof selection.quantity === "number" ? selection.quantity : min;
                    const clamped = Math.min(Math.max(current, min), max);
                    return { size_label: selection.size_label, quantity: clamped, creation_id: selection.creation_id, size_id: selection.size_id };
                })
                .filter(Boolean);

            if (filtered.length) {
                console.log('[ViewCreation] Returning filtered selections:', filtered);
                return filtered;
            }

            const initialLabel = creationSizes[0]?.size_label;
            if (!initialLabel) {
                console.log('[ViewCreation] No initialLabel found');
                return [];
            }
            const { min } = getQuantityBounds(initialLabel);
            const newSelection = [{ size_label: initialLabel, quantity: min, creation_id: creation?.creation_id, size_id: creationSizes[0]?.size_id }];
            console.log('[ViewCreation] Creating initial selection:', newSelection);
            return newSelection;
        });
    }, [creationSizes, getQuantityBounds]);

    useEffect(() => {
        if (!requiresSizeSelection || validSizeSelections.length > 0) {
            setSizeSelectionError(null);
        }
    }, [requiresSizeSelection, validSizeSelections.length]);

    const buildSizePayload = useCallback((shouldValidate = false) => {
        if (!requiresSizeSelection) return [];

        const payload = sizeSelections
            .filter((selection) => selection?.size_label && typeof selection.quantity === 'number' && selection.quantity > 0)
            .map((selection) => ({ size_label: selection.size_label, quantity: selection.quantity, creation_id: creation?.creation_id, size_id: selection.size_id }));
        if (!payload.length) {
            if (shouldValidate) {
                setSizeSelectionError('Select at least one size and quantity before continuing.');
            }
            return null;
        }

        return payload;
    }, [requiresSizeSelection, sizeSelections]);

    const handleSizeSelectionChange = useCallback((index, newLabel) => {
        setSizeSelections((prev) => prev.map((selection, idx) => {
            if (idx !== index) return selection;
            const { min } = getQuantityBounds(newLabel);
            return { size_label: newLabel, quantity: min, creation_id: creation?.creation_id, size_id: selection.size_id };
        }));
        setSizeSelectionError(null);
    }, [getQuantityBounds]);

    const handleQuantityChange = useCallback((index, delta) => {
        setSizeSelections((prev) => prev.map((selection, idx) => {
            if (idx !== index) return selection;
            const { min, max } = getQuantityBounds(selection.size_label);
            const nextQuantity = Math.min(Math.max((selection.quantity ?? min) + delta, min), max);
            return { ...selection, quantity: nextQuantity };
        }));
        setSizeSelectionError(null);
    }, [getQuantityBounds]);

    const handleAddSizeSelection = useCallback(() => {
        setSizeSelections((prev) => {
            const used = new Set(prev.map((selection) => selection.size_label));
            const available = creationSizes.find((size) => size?.size_label && !used.has(size.size_label));
            if (!available) return prev;
            const { min } = getQuantityBounds(available.size_label);
            return [...prev, { size_label: available.size_label, quantity: min, creation_id: creation?.creation_id, size_id: available?.size_id }];
        });
        setSizeSelectionError(null);
    }, [creationSizes, getQuantityBounds]);

    const handleRemoveSizeSelection = useCallback((index) => {
        setSizeSelections((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, idx) => idx !== index);
        });
        setSizeSelectionError(null);
    }, []);

    const canAddAnotherSize = useMemo(() => {
        if (!creationSizes.length) return false;
        const uniqueCount = new Set(sizeSelections.map((selection) => selection.size_label)).size;
        return uniqueCount < creationSizes.length;
    }, [creationSizes, sizeSelections]);

    const totalSelectedQuantity = useMemo(() => {
        if (!requiresSizeSelection) {
            return 1;
        }

        const total = sizeSelections.reduce((sum, selection) => {
            const qty = Number(selection?.quantity);
            if (!Number.isFinite(qty) || qty <= 0) {
                return sum;
            }
            return sum + qty;
        }, 0);

        return total > 0 ? total : 1;
    }, [requiresSizeSelection, sizeSelections]);

    // Calculate full price including shipping and fees
    const fullPriceWithShipping = useMemo(() => {
        const basePrice = parseFloat(creation?.price) || 0;
        if (basePrice === 0) return 0;

        // Calculate items total (by size if size selections exist, otherwise base price * quantity)
        let itemsTotal = 0;
        if (requiresSizeSelection && validSizeSelections.length > 0) {
            itemsTotal = validSizeSelections.reduce((sum, selection) => {
                const qty = selection.quantity || 0;
                return sum + (basePrice * qty);
            }, 0);
        } else {
            itemsTotal = basePrice * totalSelectedQuantity;
        }

        // Calculate shipping cost for physical creations
        let shippingCost = 0;
        if (isPhysical) {
            const shippingMethod = creationShippingMethod || creation?.shipping_method;
            if (shippingMethod?.rates && shippingMethod.rates.length > 0) {
                // Sort rates by sort_order to get the first/default rate
                const sortedRates = [...shippingMethod.rates].sort((a, b) => {
                    const orderA = a.sort_order ?? 0;
                    const orderB = b.sort_order ?? 0;
                    return orderA - orderB;
                });

                const firstRate = sortedRates[0];
                if (firstRate) {
                    const baseRate = parseFloat(firstRate.rate) || 0;
                    const additionalItemRate = firstRate.additional_item_rate ? parseFloat(firstRate.additional_item_rate) : 0;
                    const additionalItems = Math.max(0, totalSelectedQuantity - 1);
                    shippingCost = baseRate + (additionalItems * additionalItemRate);
                }
            }
        }

        // Creator gets = items + shipping
        const creatorGets = itemsTotal + shippingCost;

        // Calculate fees on creator gets
        const hykFee = creatorGets * HYK_FEE_PERCENT;
        const stripeFee = (creatorGets * STRIPE_FEE_PERCENT) + STRIPE_FIXED_FEE;
        const subtotal = creatorGets + hykFee + stripeFee;
        const vatAmount = subtotal * VAT_PERCENT;
        const totalAmount = subtotal + vatAmount;

        return Math.round(totalAmount * 100) / 100;
    }, [
        creation?.price,
        requiresSizeSelection,
        validSizeSelections,
        totalSelectedQuantity,
        isPhysical,
        creationShippingMethod,
        creation?.shipping_method
    ]);

    useEffect(() => {
        if ((!creation && !preview) || reload_creation) {
            if (reload_creation) {
                dispatch(reloadCreation(false))
            }
            const idToFetch = paramCreationId || searchParams.get("creation_id");
            if (idToFetch) {
                dispatch(fetchCreationById(idToFetch));
            }
        }
    }, [paramCreationId, searchParams, creation, reload_creation, dispatch]);

    // Debug log for creation data
    useEffect(() => {
        if (creation) {
            console.log('[ViewCreation] Creation data loaded:', {
                creation_id: creation.creation_id,
                category: creation.category,
                hasCreationSizes: !!creation.creation_sizes,
                creationSizesLength: creation.creation_sizes?.length,
                creationSizes: creation.creation_sizes,
                fullCreation: creation
            });
        }
    }, [creation]);

    // Load shipping addresses and set main address as default for physical creations
    useEffect(() => {
        if (isPhysical && user_id) {
            dispatch(getShippingAddresses());
        }
    }, [isPhysical, user_id, dispatch]);

    // Set main address as default when addresses load
    useEffect(() => {
        if (addresses && addresses.length > 0 && !selectedShippingAddress) {
            const mainAddress = addresses.find(addr => addr.is_main) || addresses[0];
            if (mainAddress) {
                setSelectedShippingAddress(mainAddress);
            }
        }
    }, [addresses, selectedShippingAddress]);

    // Fetch shipping method details if creation only has shipping_method_id
    useEffect(() => {
        // Reset error flag when creation changes
        if (creation?.shipping_method_id) {
            setShippingMethodFetchError(false);
        }

        // Skip if already has shipping_method object
        if (creation?.shipping_method) {
            setCreationShippingMethod(creation.shipping_method);
            return;
        }

        // Skip if no shipping_method_id
        if (!creation?.shipping_method_id) {
            setCreationShippingMethod(null);
            return;
        }

        // Skip if not physical or already loading/failed
        if (!isPhysical || loadingShippingMethod || shippingMethodFetchError) {
            return;
        }

        // Fetch shipping method
        setLoadingShippingMethod(true);

        dispatch(getShippingMethodById(creation.shipping_method_id))
            .then((result) => {
                setLoadingShippingMethod(false);
                if (result?.success && result?.response?.data) {
                    setCreationShippingMethod(result.response.data);
                    setShippingMethodFetchError(false);
                } else {
                    // If fetch failed, mark as error to prevent retries
                    setShippingMethodFetchError(true);
                    setCreationShippingMethod(null);
                    setError("Failed to load shipping method information. Please refresh the page.");
                }
            })
            .catch((error) => {
                console.error('Error fetching shipping method:', error);
                setLoadingShippingMethod(false);
                setShippingMethodFetchError(true);
                setCreationShippingMethod(null);
                setError("Failed to load shipping method information. Please refresh the page.");
            });
    }, [isPhysical, creation?.shipping_method_id, creation?.shipping_method, dispatch]);

    // Validate shipping address country when address or shipping method changes
    useEffect(() => {
        // Use creationShippingMethod (fetched or from creation) or creation.shipping_method
        const shippingMethod = creationShippingMethod || creation?.shipping_method;

        if (isPhysical && selectedShippingAddress && shippingMethod && !loadingShippingMethod) {
            const destinations = shippingMethod.destinations || [];
            const addressCountry = selectedShippingAddress.country;

            if (!addressCountry) {
                setShippingAvailabilityError('Please select a valid shipping address with a country.');
                return;
            }

            // Check if the address country is in the shipping method destinations
            const isCountryAvailable = destinations.some(destination => {
                const countryCode = destination.country_code?.toUpperCase()?.trim();
                const countryName = destination.country_name?.toUpperCase()?.trim();
                const addressCountryUpper = addressCountry?.toUpperCase()?.trim();

                if (!addressCountryUpper) return false;

                // Exact match on country code or country name
                if (countryCode && addressCountryUpper === countryCode) return true;
                if (countryName && addressCountryUpper === countryName) return true;

                // Handle common variations (e.g., "United States" vs "USA")
                if (countryName && countryName.length > 3 && addressCountryUpper.length > 3) {
                    if (countryName.includes(addressCountryUpper) || addressCountryUpper.includes(countryName)) {
                        return true;
                    }
                }

                return false;
            });

            if (!isCountryAvailable) {
                setShippingAvailabilityError('This item is not available for shipping to your selected country. Please choose a different shipping address or contact the creator.');
            } else {
                setShippingAvailabilityError(null);
            }
        } else if (isPhysical && selectedShippingAddress && !shippingMethod && !loadingShippingMethod) {
            // If physical creation has no shipping method, clear error (backend will handle)
            setShippingAvailabilityError(null);
        } else if (!isPhysical) {
            // Clear error for digital creations
            setShippingAvailabilityError(null);
        }
    }, [isPhysical, selectedShippingAddress, creationShippingMethod, creation?.shipping_method, loadingShippingMethod]);

    useEffect(() => {
        if ((preview || !creation) && init_creation) dispatch(setViewCreation(init_creation));
    }, [creations, creation]);

    // Reset scroll position when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);

        return () => {
            dispatch(setViewCreation(null));
        };
    }, []);

    const fetchLikeUnlikeCreation = (like) =>
        like
            ? fetchLikeCreation(creation.creation_id)
            : fetchUnLikeCreation(creation.creation_id);

    const handleLikeUnlike = (e) => {
        if (!sw?.requireAuth("Like/Unlike Creation", "Sign up or log in to like this creation")) {
            return;
        }
        e.preventDefault();
        const like = !(creation?.is_liked?.length > 0);

        fetchLikeUnlikeCreation(like)
            .then(() => { })
            .catch((err) => {
                console.error("Error liking/unliking: ", err);
                setError("Failed to like/unlike creation. Please try again.");
            });

        dispatch(
            likeUnlikeCreation(
                creation.creation_id,
                like
            )
        );
    };

    const handleSaveUnsave = () => {
        if (!sw?.requireAuth("save/Unsave Creation", "Sign up or log in to save this creation")) {
            return;
        }
        const unsave = creation?.is_saved?.length > 0;

        fetchSaveUnSaveCreation(creation.creation_id, unsave)
            .then(() => { })
            .catch((err) => {
                console.error("Error saving/unsaving: ", err);
                setError("Failed to save/unsave creation. Please try again.");
            });
        dispatch(
            saveUnSaveCreation(
                creation.creation_id,
                !unsave,
            )
        );
    }

    const handlePublish = async () => {
        setIsPublishing(true);
        await handleSubmit().then((res) => {
            console.log(res, "done publishing");
            // setIsPublishing(false)
        })
    }

    const handleContribute = async () => {
        setShowContribution(true);
    };

    const handleDownload = async () => {
        if (!sw?.requireAuth("Download File", "Sign up or log in to download this file")) {
            return;
        }

        if (!canDownload) {
            // If it's a paid creation and user hasn't purchased/contributed, show purchase modal
            if (isNotFree && !isOrderedBefore && !creation?.has_contributed && (!creation?.contributions || creation.contributions.length === 0)) {
                setOpenDownloadPurchaseModal(true);
                return;
            }
            // For other cases (like not logged in), show toast
            showToast("You need to purchase or contribute to download this file.", "danger");
            return;
        }

        if (isDownloading) {
            return; // Prevent multiple simultaneous downloads
        }

        // Create AbortController for timeout
        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 30000); // 30 seconds timeout

        try {
            setIsDownloading(true);
            setDownloadProgress(0);
            setError(null);
            const mediaUrl = media?.media_url || creation?.media_url;
            if (!mediaUrl) {
                const errorMessage = "File not available for download.";
                setError(errorMessage);
                showToast(errorMessage, "danger");
                setIsDownloading(false);
                return;
            }

            // Track download (if analytics endpoint exists)
            // await trackDownload(creation.creation_id);

            // Use XMLHttpRequest to track download progress
            const blob = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                // Track progress
                xhr.addEventListener('progress', (e) => {
                    if (e.lengthComputable) {
                        const percentComplete = Math.round((e.loaded / e.total) * 100);
                        setDownloadProgress(percentComplete);
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(xhr.response);
                    } else {
                        let errorMessage;
                        if (xhr.status === 404) {
                            errorMessage = "File not found. The file may have been removed or the link is invalid.";
                        } else if (xhr.status === 403) {
                            errorMessage = "Access denied. You don't have permission to download this file.";
                        } else if (xhr.status === 401) {
                            errorMessage = "Authentication required. Please log in and try again.";
                        } else if (xhr.status >= 500) {
                            errorMessage = "Server error. Please try again later.";
                        } else {
                            errorMessage = `Failed to download file: ${xhr.statusText || 'Unknown error'}`;
                        }
                        reject(new Error(errorMessage));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error("Network error. Please check your internet connection and try again."));
                });

                xhr.addEventListener('abort', () => {
                    reject(new Error("Download cancelled."));
                });

                xhr.open('GET', mediaUrl);
                xhr.responseType = 'blob';

                // Set timeout
                xhr.timeout = 30000; // 30 seconds
                xhr.addEventListener('timeout', () => {
                    xhr.abort();
                    reject(new Error("Download timeout. The file is too large or your connection is too slow. Please try again."));
                });

                xhr.send();
            });

            clearTimeout(timeoutId);
            setDownloadProgress(100);

            // Get file extension from URL or content type with comprehensive validation
            const fileExtension = getFileExtension(mediaUrl, blob.type);

            // Create a blob URL and trigger download
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            // Sanitize filename to prevent issues with special characters
            const sanitizedTitle = (creation.title || 'file')
                .replace(/[<>:"/\\|?*]/g, '') // Remove only invalid filename chars
                .replace(/\s+/g, '_') // Replace spaces with underscores
                .substring(0, 100); // Limit length
            link.download = `${sanitizedTitle}.${fileExtension}`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            // Show success toast
            showToast("File downloaded successfully!", "success");
        } catch (error) {
            console.error('Download error:', error);

            let errorMessage;
            if (error.name === 'AbortError') {
                errorMessage = "Download timeout. The file is too large or your connection is too slow. Please try again.";
            } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                errorMessage = "Network error. Please check your internet connection and try again.";
            } else if (error.name === 'NetworkError' || error.message.includes('network')) {
                errorMessage = "Network error. Please check your internet connection and try again.";
            } else {
                errorMessage = error?.message || 'Failed to download file. Please try again.';
            }

            setError(errorMessage);
            showToast(errorMessage, "danger");
        } finally {
            clearTimeout(timeoutId);
            setIsDownloading(false);
            // Reset progress after a short delay to show completion
            setTimeout(() => {
                setDownloadProgress(0);
            }, 1000);
        }
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                const shareTitle = `Check out "${creation.title}"`;

                await navigator.share({
                    title: shareTitle,
                    url: `${import.meta.env.VITE_SUPABASE_REDIRECT_URL}/creations/${creation.creation_id}`,
                });
            } catch (err) {
                console.error("Error sharing:", err);
            }
        } else {
            alert("Sharing is not supported on this device.");
        }
    };

    const handleContributeFree = async () => {
        setShowContribute(true);

    }

    const handlePaymentClick = async () => {
        if (!sw?.requireAuth("Purchase Creation", "Sign up or log in to be able to purchase this creation")) {
            try {
                // For now, only auto-resume Stripe checkout after signup for DIGITAL creations.
                // Physical purchases can require a shipping_address_id that a logged-out user doesn't have yet.
                if (media?.media_format === "physical") {
                    return;
                }
                const sizeVariants = buildSizePayload(false);
                const intentItems = Array.isArray(sizeVariants) ? sizeVariants : [];
                savePendingContributionIntent({
                    source: "order",
                    type: "order",
                    amount: creation?.price,
                    currency: "eur",
                    success_url: `${window.location.origin}/shipments`,
                    cancel_url: `${window.location.href}`,
                    creation: {
                        id: creation?.creation_id,
                        creation_id: creation?.creation_id,
                        title: creation?.title,
                        description: creation?.description,
                        creator_id: creation?.creator_id,
                        imageUrl:
                            media?.media_url ||
                            creation?.thumbnail_url ||
                            creation?.media_url,
                        creation_type:
                            media?.media_format === "physical"
                                ? PAYMENT_TYPES.purchase_physical
                                : PAYMENT_TYPES.purchase_digital,
                    },
                    items: intentItems,
                });
            } catch (e) {
                console.warn("Failed to persist pending purchase intent:", e);
            }
            return;
        }

        // Validate shipping address for physical creations
        if (isPhysical && !selectedShippingAddress) {
            setShowShippingAddressSelector(true);
            return;
        }

        // Check if shipping method supports the selected address country
        if (isPhysical && selectedShippingAddress) {
            // Use creationShippingMethod (fetched or from creation) or creation.shipping_method
            const shippingMethod = creationShippingMethod || creation?.shipping_method;

            // If creation has no shipping method, allow purchase (backend will handle validation)
            if (!shippingMethod) {
                setShippingAvailabilityError(null);
            } else {
                const destinations = shippingMethod.destinations || [];
                const addressCountry = selectedShippingAddress.country;

                if (!addressCountry) {
                    setShippingAvailabilityError('Please select a valid shipping address with a country.');
                    setShowShippingAddressSelector(true);
                    return;
                }

                // Check if the address country is in the shipping method destinations
                // Use exact matching for better accuracy
                const isCountryAvailable = destinations.some(destination => {
                    const countryCode = destination.country_code?.toUpperCase()?.trim();
                    const countryName = destination.country_name?.toUpperCase()?.trim();
                    const addressCountryUpper = addressCountry?.toUpperCase()?.trim();

                    if (!addressCountryUpper) return false;

                    // Exact match on country code or country name
                    if (countryCode && addressCountryUpper === countryCode) return true;
                    if (countryName && addressCountryUpper === countryName) return true;

                    // Handle common variations (e.g., "United States" vs "USA")
                    // Only do partial matching if both strings are reasonably long
                    if (countryName && countryName.length > 3 && addressCountryUpper.length > 3) {
                        if (countryName.includes(addressCountryUpper) || addressCountryUpper.includes(countryName)) {
                            return true;
                        }
                    }

                    return false;
                });

                if (!isCountryAvailable) {
                    setShippingAvailabilityError('This item is not available for shipping to your selected country. Please choose a different shipping address or contact the creator.');
                    setShowShippingAddressSelector(true);
                    return;
                } else {
                    setShippingAvailabilityError(null);
                }
            }
        }

        const sizeVariants = buildSizePayload(true);
        if (sizeVariants === null) {
            return;
        }

        setLoading(true);
        let isRedirecting = false;
        try {
            if (isOrderedBefore && !creation.isAddedToLibrary) {
                await addToLibrary(creation.creation_id);
                dispatch({ type: 'ADD_TO_LIBRARY', payload: { creation_id: creation.creation_id, user_id: user_id } });
                return;
            }

            if (!isNotFree) {
                handleContributeFree();
                return;
            }

            const stripe = await loadStripe(
                import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
            );
            if (!stripe) {
                setError("Unable to initialize Stripe checkout. Please try again.");
                return;
            }

            const data = {
                type: "order",
                currency: "eur",
                success_url: `${window.location.origin}/shipments`,
                cancel_url: `${window.location.href}`,
                creation: {
                    id: creation?.creation_id,
                    title: creation?.title,
                    description: creation?.description,
                    creator_id: creation?.creator_id,
                    imageUrl:
                        media?.media_url ||
                        creation?.thumbnail_url ||
                        creation?.media_url,
                    creation_type:
                        media?.media_format === "physical"
                            ? PAYMENT_TYPES.purchase_physical
                            : PAYMENT_TYPES.purchase_digital,
                },
                amount: creation.price,
                items: sizeVariants,
                shipping_address_id: isPhysical && selectedShippingAddress ? selectedShippingAddress.address_id : undefined
            };
            const sessionResponse = await createPaymentSession(data);
            const sessionId = sessionResponse?.id || sessionResponse?.data?.id;
            if (!sessionId) {
                throw new Error("Missing Stripe session id");
            }
            const result = await stripe.redirectToCheckout({
                sessionId,
            });

            if (result?.error) {
                console.error(result.error.message);
                setError(result.error.message || "Failed to redirect to checkout. Please try again.");
                return;
            }
            isRedirecting = true;
        } catch (error) {
            console.error("Create checkout session failed:", error);
            setError(
                error?.response?.message ||
                error?.message ||
                "Unable to start checkout. Please try again."
            );
        } finally {
            if (!isRedirecting) {
                setLoading(false);
            }
        }
    };

    // TODO WHEN THE RIGHT TIME COMES
    const handleSubscribeUnsubscribe = (subscribed_to_id, subscribe = true, user) => {
        if (!subscribe) {
            // HYK-1109: invalidate any pending subscribe attempt so a stale success can't
            // reopen the Contribute sheet for the creator we just unsubscribed from.
            autoContributePrompt.cancel();
        }
        dispatch({ type: 'SUBSCRIBE_SUCCESS', payload: { subscribe, subscriber_id: user_id, subscribed_to_id, user } })

        // HYK-1109: on a real (non-optimistic) new-subscribe success, auto-open the Contribute
        // sheet pre-targeted to this artist. Never gates or blocks the subscribe action itself.
        const subscribeAttempt = autoContributePrompt.beginSubscribeAttempt();
        dispatch(fetchSubscribe(subscribed_to_id, subscribe)).then(({ success, alreadySubscribed }) => {
            // HYK-1138: the optimistic update above bumped the count for a follow that already existed; refetch.
            if (alreadySubscribed) dispatch(reloadCreation(true));
            if (autoContributePrompt.evaluate(subscribed_to_id, { success, eligible: subscribe && !alreadySubscribed, startedAtGeneration: subscribeAttempt })) {
                setShowContribution(true);
            }
        });
    };

    const handleDeleteCreation = () => {
        fetchDeleteCreation(creation.creation_id).then(() => {
            dispatch({ type: 'DELETE_CREATION', payload: { creation_id: creation.creation_id } });
            setOpenDeleteCreation(false);
            setOpenCreationMenu(false);
            navigate(-1);
        }).catch((err) => {
            console.error("Error deleting creation:", err);
            setError("Failed to delete creation. Please try again.");
            setOpenDeleteCreation(false);
        });
    }

    const addCreationToLibrary = async () => {
        if (isNotFree) {
            setOpenNotFree(true);
        } else {
            const sizeVariants = buildSizePayload(true);
            if (sizeVariants === null) {
                return;
            }

            const totalQuantity = sizeVariants.length
                ? sizeVariants.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
                : 1;

            createOrderForFreeCreationOnly(creation.creation_id, sizeVariants, totalQuantity)
                .then(async () => {
                    setDisablePurchaseButton(false);
                    setLoading(false);
                    await addToLibrary(creation.creation_id);
                    dispatch({ type: 'ADD_TO_LIBRARY', payload: { creation_id: creation.creation_id, user_id: user_id } });
                    setToast(true);

                    setTimeout(() => setToast(false), 3000);
                    // navigate("/library");
                })
                .catch((err) => {
                    console.error("Error adding creation to library:", err);
                    setError("Failed to add creation to library. Please try again.");
                    setDisablePurchaseButton(false);
                    setLoading(false);
                });
        }
    }

    const removeFromLibrary = () => {
        deleteFromLibrary(creation.creation_id)
            .then(() => {
                console.log("done removing from library");
            })
            .catch((err) => {
                console.error("Error removing creation from library:", err);
                setError("Failed to remove creation from library. Please try again.");
            });
        dispatch({ type: 'DELETE_FROM_LIBRARY', payload: { creation_id: creation.creation_id, user_id: user_id } });
        setOpenRemove(false)
    }

    const clickSubscribe = () => {
        if (!sw?.requireAuth("Subscribe to Creation", "Sign up or log in to subscribe to this creator")) {
            return;
        }
        creation.users?.is_subscribed ? setShowUnsubscribe(true) : handleSubscribeUnsubscribe(creation.users?.user_id, !creation.users?.is_subscribed)
    }
    const clickComment = () => {
        if (!sw?.requireAuth("Comment on Creation", "Sign up or log in to be able to comment on this creation")) {
            return;
        }
        if (!preview) setShowComments(true)
    }

    const isLimitedEdition = !!(media?.max_stock && media?.max_stock > 0);
    const isLimitedEditionAvailable =
        isLimitedEdition && !!(media?.current_stock && media?.current_stock > 0);

    const isNotFree = creation ? parseFloat((creation || {})?.price || 0) > 0 : false;

    // Check if user can download (must be logged in, creation must allow download, and if paid, must have purchased/contributed)
    const canDownload = useMemo(() => {
        if (!media?.allow_download) return false;
        if (!isDigital) return false;
        if (!user_id) return false; // Must be logged in

        // Free creation: can download
        if (!isNotFree) return true;

        // Paid creation: only if the current user has purchased or contributed (not just "any" orders/contributions)
        const orders = creation?.orders;
        const hasPurchasedByCurrentUser = (() => {
            if (!orders?.length) return false;
            const first = orders[0];
            if (first && (typeof first.user_id !== 'undefined' || typeof first.buyer_id !== 'undefined')) {
                return orders.some((o) => (o.user_id || o.buyer_id) === user_id);
            }
            return !!orders.length; // Backend returns only current user's orders
        })();

        const contributions = creation?.contributions;
        const hasContributedByCurrentUser =
            creation?.has_contributed === true ||
            (Array.isArray(contributions) && contributions.some((c) => (c.user_id || c.buyer_id) === user_id));

        return hasPurchasedByCurrentUser || hasContributedByCurrentUser;
    }, [media?.allow_download, isDigital, user_id, isNotFree, creation?.orders, creation?.contributions, creation?.has_contributed]);

    const disableActionButton = (isLimitedEdition && !isLimitedEditionAvailable && !isOrderedBefore) || disablePurchaseButton || (creation?.isAddedToLibrary && (isOrderedBefore || !isNotFree))

    const shouldShowBuyNowButton =
        isNotFree &&
        !creation?.isAddedToLibrary &&
        !fromProfile &&
        (!isLimitedEdition || isLimitedEditionAvailable);

    const shouldShowSoldOutButton =
        isLimitedEdition && !isLimitedEditionAvailable;

    const shouldShowPublishButton =
        !!preview;

    const shouldShowPurchaseContainer =
        shouldShowBuyNowButton || shouldShowSoldOutButton || shouldShowPublishButton;
    // Generate SEO data for the creation
    const generateCreationSEOData = () => {
        if (!creation || preview) return {};

        const creatorName = getUserDisplayName(creation.users);
        const creatorUsername = creation.users?.userName
            ? (creation.users.userName.startsWith('@') ? creation.users.userName : `@${creation.users.userName}`)
            : '';

        // Generate title
        const title = `${creation.title} by ${creatorName || creatorUsername} - HYK`;

        // Generate description from creation description or default
        const description = creation.description
            ? creation.description.length > 160
                ? creation.description.substring(0, 157) + '...'
                : creation.description
            : `Check out this amazing ${creation.tag || 'creation'} by ${creatorName || creatorUsername} on HYK`;

        // Get thumbnail image
        const image = creation.thumbnail_url;

        // Generate current URL
        const currentUrl = `${window.location.origin}/creations/${creation.creation_id}`;

        return {
            title,
            description,
            image,
            url: currentUrl,
            type: 'article'
        };
    };

    const getCategoryIcon = useMemo(() => {
        const categoryLower = creation?.category?.toLowerCase();
        return GENRE_ICONS[categoryLower] || 'creations';
    }, [creation?.category]);

    const reported = !(!creation?.user_report || creation?.user_report?.status === 'rejected')
    return creation ? (
        <div className="container-creation" onClick={() => setOpenMenu(false)}>
            {/* {!preview && <SEOHead {...generateCreationSEOData()} />} */}
            {openCreationMenu &&
                <EditCreation
                    creation={creation}
                    onClose={() => setOpenCreationMenu(false)}
                    onDelete={() => setOpenDeleteCreation(true)}
                    onEdit={() => navigate(`/creations/${creation.creation_id}/edit`)}
                />
            }
            {openDeleteCreation &&
                <DeleteCreation
                    creation={creation}
                    onClose={() => setOpenDeleteCreation(false)}
                    onDelete={handleDeleteCreation}
                />
            }
            {/* {openModifyCreation && <ModifyCreation
                creation={creation}
                onClose={() => { setOpenModifyCreation(false); setReload(true) }}
                onEditMedia={() => setOpenEditMedia(true)}
            />} */}
            {openEditMedia && <EditMediaPreview
                creation_id={creation.creation_id}
                onClose={() => setOpenEditMedia(false)}
            />}
            {showContribution && (
                <ContributionSection
                    creation={{
                        ...creation,
                        title: `Creator ${creation?.users?.userName}`,
                    }}
                    onClose={() => {
                        autoContributePrompt.handleClose();
                        setShowContribution(false);
                    }}
                />
            )}
            {showSuccessContribution && (
                <ContributionSuccess
                    onClose={() => {
                        setShowSuccessContribution(false);
                        searchParams.delete("success");
                        navigate(`${location.pathname}?${searchParams.toString()}`, {
                            replace: true,
                        });
                    }}
                />
            )}
            {showComments && !preview && (
                <CommentSection
                    creation_id={creation.creation_id}
                    type="creations"
                    onClose={() => setShowComments(false)}
                />
            )}
            {showReport && !preview && (
                <ReportCreationSheet
                    onClose={() => setShowReport(false)}
                    creation_id={creation.creation_id}
                />
            )}
            {showFullBio && <AboutCreator user={creation.users} onClose={() => setShowFullBio(false)} />}
            {showUnsubscribe && <Unsubscribe user={creation.users} onClose={() => setShowUnsubscribe(false)} onSubmit={() => { handleSubscribeUnsubscribe(creation.users?.user_id, !creation.users?.is_subscribed, creation.users); setShowUnsubscribe(false) }} />}
            {showPriceDetails && <PriceDetails
                creation={creation}
                onClose={() => setShowPriceDetails(false)}
                onContribute={() => { handleContribute(); setShowContribute(false) }}
                quantity={totalSelectedQuantity}
                isPhysical={isPhysical}
                shippingMethod={creationShippingMethod || creation?.shipping_method}
                sizeSelections={validSizeSelections}
                requiresSizeSelection={requiresSizeSelection}
            />}
            {showContribute && <Contribute creation={creation} onClose={() => setShowContribute(false)} onSubmit={() => { handleContribute(); setShowContribute(false) }} />}
            {toast && (
                <div
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#333",
                        color: "#fff",
                        padding: "12px 20px",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        animation: "fadeIn 0.3s ease",
                    }}
                >
                    ✅ Added to your Library
                </div>
            )}
            {openNotFree && <ModalMessage
                children={<div className="d-flex f-column aic jcc w-full">
                    <p className="fs-sm2 fw-bold mb-40 mt-32 ta-center">
                        You need to purchase this creation
                        before adding it to your library</p>
                    <button
                        className="btn red-btn btn-full aic jcc fw-extra-bold mb-24"
                        style={{ height: '64px' }}
                        onClick={() => handlePaymentClick()}
                    >
                        BUY NOW
                    </button>
                    <p className="fs-xxs fg-dark fw-bold mb-16 mt-0 c-pointer td-underline" onClick={() => setOpenNotFree(false)}>CANCEL</p>
                </div>}
                onClose={() => setOpenNotFree(false)}

            />}
            {openPurchaseAfterPreview && <ModalPurchaseAfterPreview
                onClose={() => setOpenPurchaseAfterPreview(false)}
                onPurchase={() => handlePaymentClick()}
                price={creation.totalAmount.toFixed(2)}
                currency={creation.currency || "€"}
            />}
            {openDownloadPurchaseModal && <ModalMessage
                children={<div className="d-flex f-column aic jcc w-full">
                    <Icon alias="purchase_bag" width={80} height={80} color="#FF3232" />
                    <p className="fs-sm2 fw-bold mb-24 mt-32 ta-center">
                        You need to purchase this creation before downloading files
                    </p>

                    {/* File details box */}
                    {(() => {
                        const mediaUrl = media?.media_url || creation?.media_url;
                        const fileName = creation?.title || 'File';
                        const fileExtension = mediaUrl?.split('.').pop()?.split('?')[0]?.toUpperCase() || '';
                        // Truncate filename to match design (showing first part)
                        const truncatedFileName = fileName.length > 25 ? fileName.substring(0, 22) + '...' : fileName;

                        return (
                            <div style={{
                                width: '100%',
                                backgroundColor: '#F5F5F5',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                marginBottom: '24px',
                                border: '1px solid #E5E5E5'
                            }}>
                                <p className="fs-regular fw-medium fg-dark mb-0" style={{
                                    fontSize: '14px',
                                    lineHeight: '22px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {truncatedFileName}
                                </p>
                                <div className="d-flex aic jcsb mt-4">
                                    <span className="fs-xs fg-dark" style={{ fontSize: '12px' }}>{fileExtension}</span>
                                </div>
                            </div>
                        );
                    })()}

                    <button
                        className="btn red-btn btn-full aic jcc fw-extra-bold mb-24"
                        style={{ height: '64px' }}
                        onClick={() => {
                            setOpenDownloadPurchaseModal(false);
                            handlePaymentClick();
                        }}
                    >
                        <span>
                            BUY NOW
                            {parseFloat(creation.price) > 0 && (
                                <p className="fs-tiny fw-medium fg-white mt-2 ta-center tt-capitalize mb-0">
                                    Secured by Stripe
                                </p>
                            )}
                        </span>
                    </button>
                    <p className="fs-xxs fg-dark fw-bold mb-16 mt-0 c-pointer td-underline"
                        onClick={() => setOpenDownloadPurchaseModal(false)}>
                        CANCEL
                    </p>
                </div>}
                onClose={() => setOpenDownloadPurchaseModal(false)}
            />}
            {openRemove && <ModalMessage
                children={<div className="d-flex f-column aic jcc w-full">
                    <p className="fs-sm2 fw-bold mb-40 mt-32">
                        Remove from your library?
                    </p>
                    <button className="btn red-outline-btn btn-full aic jcc fw-extra-bold mb-24" style={{ height: '64px' }} onClick={removeFromLibrary}>YES, REMOVE</button>
                    <p className="fs-xxs fg-dark fw-bold mb-16 mt-0 c-pointer td-underline" onClick={() => setOpenRemove(false)}>KEEP IN LIBRARY</p>
                </div>}
                onClose={() => setOpenRemove(false)}
            />}
            {showShippingAddressSelector && (
                <ShippingAddressSelector
                    onSelect={(address) => {
                        setSelectedShippingAddress(address);
                        setShowShippingAddressSelector(false);
                        // Validation will happen automatically via useEffect
                    }}
                    onClose={() => {
                        setShowShippingAddressSelector(false);
                    }}
                    selectedAddressId={selectedShippingAddress?.address_id}
                />
            )}

            {!preview && (
                <div className="header-creation d-flex f-row">
                    <span className="d-flex" onClick={() => handleGoBackRedirection(navigate, location.pathname)}>
                        <Icon alias={"chevron-left"} color="#0A0F1E" width={24} />
                        {<span className="fw-bold">{(creation?.users?.userName || getUserDisplayName(creation?.users)).toUpperCase()}</span>}
                    </span>
                    <div className="ml-auto d-flex aic">
                        {fromProfile && <span onClick={() => navigate(`/creations/${creation.creation_id}/edit`)} className='bg-bg-grey icon-container mr-16'>
                            <Icon alias={"edit_v2"} stroke="#FF3232" color='none' width={24} />
                        </span>
                        }
                        <span className="bg-bg-grey icon-container mr-16 d-flex aic jcc" onClick={() => creation.isAddedToLibrary ? setOpenRemove(true) : addCreationToLibrary()}>
                            {creation.isAddedToLibrary ?
                                <Icon alias={"check_v2"} stroke="#FF3232" color='none' width={15} className={"pt-2"} />
                                : <Icon alias={"plus_v2"} stroke="#FF3232" color='none' width={20} />}
                        </span>
                        <span className={`d-flex p-relative`} onClick={(e) => { e.stopPropagation(); setOpenMenu(!openMenu) }}>
                            <span className='bg-bg-grey icon-container'><Icon alias={"three_dots"} strokeWidth={2} stroke="#FF3232" width={24} /></span>
                            {openMenu && (
                                <div className="news-menu-dropdown" >
                                    {/* <div className="news-menu-overlay" onClick={() => setOpenNewsMenu(false)}></div> */}
                                    <div className="news-menu-content">
                                        <div className="news-menu-item" onClick={() => creation.isAddedToLibrary ? setOpenRemove(true) : addCreationToLibrary()}>
                                            <span>{creation.isAddedToLibrary ? `Remove from` : `Add to`} library</span>
                                            <Icon alias={creation.isAddedToLibrary ? "minus" : "plus_v2"} stroke="#0A0F1E" color='none' width={20} />
                                        </div>
                                        <div className="news-menu-item" onClick={(e) => { e.stopPropagation(); handleNativeShare() }}>
                                            <span>Share Creation</span>
                                            <Icon alias={"share"} stroke="#0A0F1E" color='none' width={20} />
                                        </div>
                                        <div className="news-menu-divider"></div>
                                        <div className="news-menu-item" onClick={() => setShowReport(true)}>
                                            <span className={`${reported ? "fg-grey7" : "fg-black"}`}>{reported ? "Reported" : "Report"}</span>

                                            <Icon alias={"report_v2"} stroke={reported ? "#777" : "#0A0F1E"} color='none' width={20} />
                                        </div>
                                        {fromProfile &&
                                            <Fragment>
                                                <div className="news-menu-divider" style={{ height: '4px' }}></div>

                                                <div className="news-menu-item" onClick={() => setOpenDeleteCreation(true)}>
                                                    <span>Delete creation</span>
                                                    <Icon alias={"trash_v2"} stroke="#0A0F1E" color='none' width={20} />
                                                </div>
                                            </Fragment>}
                                    </div>
                                </div>
                            )}
                        </span>
                    </div>
                </div>
            )}
            <div className="creation-detail__main">
            <MetaTags
                title={`${creation?.title} - HYK Creation`}
                description={htmlToPlainTextServer(creation?.description) || "Discover this amazing creation on HYK"}
                image={getOGImageUrl('creation', {
                    title: creation?.title || 'HYK Creation',
                    author: creation?.users?.userName || getUserDisplayName(creation?.users) || '',
                    image: creation?.thumbnail_url || '',
                    description: htmlToPlainTextServer(creation?.description) || '',
                    id: creation?.creation_id || ''
                })}
                url={`${window.location.origin}/creations/${creation?.creation_id}`}
                type="article"
            />
            <div
                className={`thumbnail-creation ${mediaArray.length > 0 && mediaArray[0]?.media_type === "pdf" ? "pdf-thumbnail" : ""
                    }`}
                key={creation.creation_id}
            >
                {mediaArray.length > 0 ? (
                    // For non-music DIGITAL creations with multiple media, show carousel (independent of signed_url).
                    (!isMultiTrackMusic &&
                        isDigital &&
                        String(creation?.category || '').toLowerCase() !== 'music' &&
                        mediaArray.length > 1) ? (
                    <MediaCarousel
                        mediaArray={mediaArray}
                        thumbnailUrl={creation.thumbnail_url}
                        isPreview={isNotFree && !isOrderedBefore && isDigital}
                        isNotFree={isNotFree}
                        isPhysical={isPhysical}
                        setOpenPurchaseAfterPreview={setOpenPurchaseAfterPreview}
                    />
                    ) : selectedTrack?.signed_url ? (
                    <MediaPlayer
                        // Keep MediaPlayer instance stable across track switches.
                        // Forcing remount via key can trigger Video.js DOM teardown while React is also deleting nodes,
                        // causing `removeChild` NotFoundError when switching media quickly.
                        key={`${creation.creation_id}-media-player`}
                        mediaUrl={selectedTrack.signed_url}
                        streamUrl={selectedTrackStreamUrl}
                        streamMimeType={selectedTrackStreamMimeType}
                        mimeType={selectedTrack.mime_type}
                        fileType={selectedTrack.media_type}
                        isPreview={isNotFree && !isOrderedBefore && isDigital}
                        isPreviewfromViewCreation={isNotFree && !isOrderedBefore && isDigital}
                        posterImage={creation.thumbnail_url}
                        setOpenPurchaseAfterPreview={setOpenPurchaseAfterPreview}
                        isNotFree={isNotFree}
                        isPhysical={selectedTrack?.media_format === "physical"}
                        initialTime={initialTime}
                        autoPlay={shouldAutoplaySelectedTrack}
                        onPlaybackEnded={handlePlaybackEnded}
                    />
                    ) : (
                    <MediaCarousel
                        mediaArray={mediaArray}
                        thumbnailUrl={creation.thumbnail_url}
                        isPreview={isNotFree && !isOrderedBefore && isDigital}
                        isNotFree={isNotFree}
                        isPhysical={isPhysical}
                        setOpenPurchaseAfterPreview={setOpenPurchaseAfterPreview}
                    />
                    )
                ) : creation.thumbnail_url || creation.media_url ? (
                    <img
                        src={creation.thumbnail_url || creation.media_url}
                        alt={creation.title || "Creation preview"}
                        className="img-thumbnail"
                    />
                ) : null}
            </div>
            <div className="interact-creation d-flex f-row aic mb-8">
                <span className="d-flex" onClick={(e) => !preview ? handleLikeUnlike(e) : null}>{!preview && !creation?.is_liked?.length ? <Icon alias={"like"} stroke="#0A0F1E" color='#0A0F1E' width={24} /> : <Icon alias={"liked_v2"} width={24} height={24} color='#FF8CDC' />}</span>
                <p className="fs-xxs fg-crea ml-8 fw-medium-bold">{!preview ? (creation?.total_likes || '') : ''}</p>
                <span className="d-flex" onClick={clickComment}><Icon alias={"comment"} stroke="#0A0F1E" color='none' width={24} className={"ml-16"} /></span>
                <p className="fs-xxs fg-crea ml-8 fw-medium-bold">{!preview ? (creation.total_comments || '') : ''}</p>
                <span className="d-flex ml-16" onClick={handleSaveUnsave}>
                    {!creation?.is_saved?.length ?
                        <Icon alias={"star"} strokeWidth={2} color="#0A0F1E" width={24} />
                        : <Icon alias={"saved_star"} stroke='#FF8CDC' color='#FF8CDC' width={24} />}
                </span>
                <span className="d-flex" onClick={() => !preview && handleNativeShare()}><Icon alias={"share"} stroke="#0A0F1E" color='none' width={24} className={"ml-24"} /></span>
                {canDownload && (
                    <span className="d-flex ml-16" onClick={handleDownload} title="Download file">
                        <Icon alias={"chevron-down"} stroke="#0A0F1E" color='none' width={24} />
                    </span>
                )}
                {!fromProfile && <button onClick={handleContribute} className="contribute-btn btn-sm btn-xs red-outline-btn d-flex aic ml-auto fs-tiny fw-medium">
                    <Icon alias={"credit_card"} color="#FF3232" stroke={'#FF3232'} width={16} className={'mr-4'} />
                    <span className='fs-tiny fw-extra-bold'>Contribute</span>
                </button>}
            </div>
            <div className="px-16" style={{ maxWidth: '100dvw' }}>
                <div className='d-flex aifs f-column profile-card'>
                    <div className='d-flex aic w-100'>
                        <div style={{ minWidth: '40px', minHeight: '40px' }}>
                            <ProfilePicture imgUrl={creation.users?.profilePicture} width={40} height={40} no_edit={true} handleClick={() => {
                                const p = getProfilePublicPath(creation.users);
                                if (p) navigate(p);
                            }} />
                        </div>
                        <div className='ml-12' onClick={() => {
                            const p = getProfilePublicPath(creation.users);
                            if (p) navigate(p);
                        }}>
                            <p className='my-0 fs-xs fw-bold fg-dark'>{getUserDisplayName(creation.users) || creation.users?.userName || ''}</p>

                            {/* <p className='my-0 fs-xs fg-hyk-medgrey'>{formatTimeAgo(view_news.created_at)}</p> */}
                        </div>
                        {!fromProfile && <button
                            className={`subscribe-btn ml-auto ${!creation.users?.is_subscribed
                                ? "unsubscribed"
                                : "subscribed"
                                }`}
                            onClick={clickSubscribe}>
                            {creation.users?.is_subscribed ? 'SUBSCRIBED' : 'SUBSCRIBE'}
                        </button>}
                    </div>
                    <div className='profile-bio'>
                        <div className='my-0 fs-real fw-medium fg-black bio-text-wrap'>
                            <BioPreview text={creation.users?.bio} onMore={() => setShowFullBio(true)} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="mx-16 mb-16">
                <p className="fs-md fg-dark my-16 fw-medium-bold">{creation.title || "Untitled"}</p>

                <div className="d-flex aic fs-real fw-medium">
                    <Icon
                        alias={getCategoryIcon}
                        width={32}
                        color="#0A0F1E"
                    />
                    <span className="ml-8">{capitalizeFirstLetter(
                        (Array.isArray(media) ? media[0]?.media_format : media?.media_format)
                            ? (Array.isArray(media) ? media[0]?.media_format : media?.media_format)
                            : (Array.isArray(media) ? media[0]?.media_url : media?.media_url)
                                ? "Digital"
                                : "Physical"
                    )}{" "}</span>
                    <span className="dot"></span>
                    <span className="mb-0">
                        {media?.max_stock ? (media?.max_stock > 1 ? "Limited" : "Unique") : "Unlimited"} edition
                        {media?.max_stock > 0
                            ? `, ${media.current_stock || 0}/${media.max_stock || 0
                            } available`
                            : ""}
                    </span>
                </div>

                {/* <p className="fs-xs fg-grey fw-medium my-0">{`${creation.users?.firstName || ""
                    } ${creation.users?.lastName || ""}`}</p> */}

                {/* <p className="fg-grey fs-xs fw-medium my-0">
                    {capitalizeFirstLetter(
                        media?.media_format
                            ? media?.media_format
                            : media?.media_url
                                ? "Digital"
                                : "Physical"
                    )}{" "}
                    Copy
                </p> */}

            </div>
            <div className="tags-container d-flex aic mx-16 mb-24">
                <p className="category-tag primary">{capitalizeFirstLetter(creation.tag)}</p>
                {creation.tags && creation.tags.length > 0 && (
                    creation.tags
                        .filter(tag => !tag.is_primary)
                        .map(
                            (tag) => <span className="category-tag" key={tag.name}>
                                {`#${capitalizeFirstLetter(
                                    typeof tag === "string" ? tag : tag?.name
                                ).trim()}`}
                            </span>
                        )
                )}
            </div>

            </div>
            <div className="creation-detail__sidebar">
            <div className="price-container d-flex f-column mx-16 p-16">
                {isNotFree ? <div className='d-flex f-column jcc w-100'>
                    <span
                        className="fg-pink fw-medium d-flex aic fs-regular fw-bold price-info-clickable "
                        onClick={() => setShowPriceDetails(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setShowPriceDetails(true)}
                        aria-label="View detailed price breakdown"
                    >
                        € {fullPriceWithShipping.toFixed(2)}
                        <Icon alias={"info_v2"} color="#FF8CDC" width={16} className="ml-4" />
                    </span>
                    {(() => {
                        const hasCreationSizes = creation?.creation_sizes && creation?.creation_sizes.length > 0;
                        const hasSizeSelections = sizeSelections.length > 0;
                        const shouldShow = hasCreationSizes && hasSizeSelections;
                        console.log('[ViewCreation] Size selector render check:', {
                            hasCreationSizes,
                            creationSizesLength: creation?.creation_sizes?.length,
                            hasSizeSelections,
                            sizeSelectionsLength: sizeSelections.length,
                            sizeSelections: sizeSelections,
                            shouldShow,
                            creation: creation ? { id: creation.creation_id, category: creation.category } : null
                        });
                        return shouldShow;
                    })() && (
                            <>
                                <div className="pricecontdevider"></div>
                                <div className="size-selector">
                                    {sizeSelections.map((selection, index) => {
                                        const { min, max } = getQuantityBounds(selection.size_label);
                                        const takenLabels = sizeSelections
                                            .filter((_, idx) => idx !== index)
                                            .map((sel) => sel.size_label);
                                        const stockInfo = getStockInfo(selection.size_label);
                                        const isOutOfStock = max === 0 || stockInfo.isOutOfStock;

                                        return (
                                            <div className="size-selector__row" key={`${selection.size_label || 'size'}-${index}`}>
                                                <div className="size-selector__select">
                                                    <div className="d-flex aic" style={{ position: 'relative', width: '100%', minHeight: '40px' }}>
                                                        <label className="fs-regular fw-medium fg-dark d-flex aic" style={{ cursor: 'pointer', flex: 1, pointerEvents: 'none' }}>
                                                            Size {selection.size_label}
                                                            <Icon alias="chevron-down" width={16} color="#1C1B1F" className="ml-4" />
                                                        </label>
                                                        <select
                                                            value={selection.size_label || ''}
                                                            onChange={(event) => handleSizeSelectionChange(index, event.target.value)}
                                                            className="size-selector__select-input fs-regular fw-medium fg-dark"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                opacity: 0,
                                                                cursor: 'pointer',
                                                                zIndex: 1
                                                            }}
                                                        >
                                                            {creationSizes.map((size) => {
                                                                let optionStock = size.stock_quantity;
                                                                if (typeof optionStock === "string" && optionStock !== "") {
                                                                    const parsedStock = Number(optionStock);
                                                                    optionStock = Number.isNaN(parsedStock) ? null : parsedStock;
                                                                }
                                                                const isOptionOut = optionStock === 0;
                                                                return (
                                                                    <option
                                                                        key={size.size_label}
                                                                        value={size.size_label}
                                                                        disabled={takenLabels.includes(size.size_label) || (isOptionOut && selection.size_label !== size.size_label)}
                                                                        className="fs-regular fw-medium fg-dark"
                                                                    >
                                                                        {size.size_label}
                                                                        {isOptionOut ? ' (out of stock)' : ''}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="size-selector__actions">
                                                    <div className="size-selector__qty">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleQuantityChange(index, -1)}
                                                            disabled={selection.quantity <= min}
                                                        >
                                                            –
                                                        </button>
                                                        <span>{selection.quantity ?? min}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleQuantityChange(index, 1)}
                                                            disabled={selection.quantity >= max}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                    <div className={`size-selector__stock ${stockInfo.isOutOfStock ? 'is-out' : ''}`}>{stockInfo.label}</div>
                                                    {sizeSelections.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="size-selector__remove"
                                                            onClick={() => handleRemoveSizeSelection(index)}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {sizeSelectionError && (
                                        <p className="size-selector__error">{sizeSelectionError}</p>
                                    )}
                                    <button
                                        type="button"
                                        className={`size-selector__add ${!canAddAnotherSize ? 'disabled' : ''}`}
                                        onClick={handleAddSizeSelection}
                                        disabled={!canAddAnotherSize}
                                    >
                                        <span>+ Size</span>
                                    </button>
                                </div>
                            </>
                        )}

                </div> :
                    <span
                        className="fw-bold fg-pink d-flex aic price-info-clickable"
                        onClick={() => setShowPriceDetails(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setShowPriceDetails(true)}
                        aria-label="Learn about contributing to free creations"
                    >
                        <p className="my-0">FREE</p>
                        <Icon alias={"info_v2"} color="#FF8CDC" width={16} className="ml-4" />
                    </span>
                }
                {/* Download Section - Only when allow_download enabled (independent from playlist/media display) */}
                {isDigital && media?.allow_download && isNotFree && (
                    <div className="mt-16 w-full">
                        <div className="download-section hyk914-track-card">
                            <div className="hyk914-track-card__download-wrap">
                            <div className="hyk914-track-card__download-row d-flex f-row aic jcsb">
                                <p className="fs-regular fw-bold fg-dark mb-0" style={{ fontSize: '14px', lineHeight: '20px' }}>
                                    {isDownloading ? "Downloading..." : canDownload ?
                                        ((isOrderedBefore || !isNotFree) ? "Download your file" : "Available for download")
                                        : "Available for download"
                                    }
                                </p>
                                {isDownloading && (
                                    <CircularProgress
                                        showprogress={false}
                                        progress={downloadProgress}
                                        size={24}
                                        strokeWidth={2}
                                        color="#FF3232"
                                    />
                                )}
                                {error && <Icon alias={"error_triangle"} width={24} color="#C9100E" />}

                                {!isDownloading && (
                                    <button
                                        onClick={handleDownload}
                                        className="btn red-outline-btn d-flex aic jcc"
                                        style={{
                                            padding: '8px 16px',
                                            minWidth: '126px',
                                            height: '32px',
                                            gap: '8px',
                                            opacity: isDownloading ? 0.6 : 1,
                                            cursor: isDownloading ? 'wait' : 'pointer'
                                        }}
                                    >
                                        {error ? (
                                            <span className="fs-tiny fw-extra-bold">Retry</span>
                                        ) : (
                                            <>
                                                <Icon alias={"arrow_down"} color='#FF3232' width={16} height={16} />
                                                <span className="fs-tiny fw-extra-bold">Download</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Music tracklist is independent from allow_download */}
                {isMultiTrackMusic && (
                    <div className="mt-16 w-full">
                        <div className="hyk914-track-card">
                            <TrackList
                                tracks={mediaArray}
                                selectedIndex={selectedTrackIndex}
                                onSelectTrack={(idx) => {
                                    if (idx !== selectedTrackIndex) {
                                        setSelectedTrackIndex(idx);
                                    }
                                    setShouldAutoplaySelectedTrack(true);
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Shipping Address Section - Only for Physical Creations */}
            {isPhysical && isNotFree && (
                <div className="mx-16 mb-24 mt-16">
                    <div className="shipping-address-section">
                        <p className="fs-xs fw-bold fg-dark mb-8">Shipping address</p>
                        {selectedShippingAddress ? (
                            <>
                                <div
                                    className="shipping-address-display c-pointer"
                                    onClick={() => setShowShippingAddressSelector(true)}
                                    style={{
                                        border: shippingAvailabilityError ? '1px solid #FF3232' : 'none'
                                    }}
                                >
                                    <div className="shipping-address-content">
                                        <p className="fs-xs fg-dark fw-medium mb-0">
                                            {selectedShippingAddress.address_line_1}
                                            {selectedShippingAddress.address_line_2 && `, ${selectedShippingAddress.address_line_2}`}
                                            {selectedShippingAddress.city && `, ${selectedShippingAddress.city}`}
                                            {selectedShippingAddress.postal_code && ` ${selectedShippingAddress.postal_code}`}
                                            {selectedShippingAddress.country && `, ${selectedShippingAddress.country}`}
                                        </p>
                                    </div>
                                    <Icon alias="chevron-right" width={20} color="#1C1B1F" />
                                </div>
                                {shippingAvailabilityError && (
                                    <p className="fs-xs fg-red-pink fw-medium mt-8 mb-0">
                                        {shippingAvailabilityError}
                                    </p>
                                )}
                            </>
                        ) : (
                            <div className="shipping-address-empty">
                                <button
                                    className="btn red-outline-btn"
                                    onClick={() => setShowShippingAddressSelector(true)}
                                    style={{ height: '48px', width: '100%' }}
                                >
                                    + ADDRESS
                                </button>
                                <p className="fs-xxs fg-red-pink fw-bold mt-8 mb-0">
                                    You must add your delivery address
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="desc-container mx-16">
                <p className="fs-sm2 fg-dark fw-medium-bold mb-8 mt-24">About</p>
                <p className="fs-real fg-dark fw-medium mb-0 mt-0" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(creation.description || "") }}></p>
            </div>
            {creation?.credits && <div className="mx-16 mt-24">
                <p className="fs-sm2 fg-dark fw-medium-bold mb-8 ">Credits</p>
                <p className={`fs-real fg-dark fw-medium mb-0 mt-0 overflow-hidden transition-all duration-300 ${creditsExpanded ? "line-clamp-none" : "line-clamp-2"
                    }`}>{creation?.credits}</p>

            </div>}
            {/* <div className="about-container pb-24 mx-16 mb-32">
                <p className="fs-sm2 fg-grey fw-medium-bold mt-0 mb-16">
                    About the creation
                </p>
                <p className="fg-crea fs-xs fw-medium my-0">
                    {creation.description || ""}
                </p>
                <div className="d-flex aic my-12">
                    <p className="category-tag fs-tiny fw-extra-bold mt-0 mb-12 mr-8">
                        {capitalizeFirstLetter(creation.tag)}
                    </p>
                    <p className="category-tag fs-tiny fw-extra-bold mt-0">
                        {capitalizeFirstLetter(creation.category)}
                    </p>
                </div>
                <p className="my-0">
                    {creation.tags && creation.tags.length > 0 && (
                        <span className="fg-tag-blue fs-xs fw-medium">
                            {creation.tags
                                .map(
                                    (tag) =>
                                        `#${capitalizeFirstLetter(
                                            typeof tag === "string" ? tag : tag?.name
                                        ).trim()}`
                                )
                                .join(" ")}
                        </span>
                    )}
                </p>
            </div> */}
            {/* <div className="creator-container mx-16">
                <p className="fs-sm2 fg-grey fw-medium-bold mt-0 mb-16">
                    About this creator
                </p>
                <div className="creator-info d-flex f-row aic mb-24">
                    <ProfilePicture
                        imgUrl={creation.users?.profilePicture}
                        width={56}
                        height={56}
                        no_edit={true}
                        // GET THIS BACK IT"S JUST FOR TESTING PURPOSES
                        // handleClick={() => navigate(creation.users?.user_id === user_id ? `/profile` : `/user-profile?user_id=${creation.users?.user_id}`)}
                        handleClick={() => {
                            const p = getProfilePublicPath(creation.users);
                            if (p) navigate(p);
                        }}
                    />
                    <p
                        className="fs-regular fg-black ml-16 my-0"
                        onClick={() => {
                            const p = getProfilePublicPath(creation.users);
                            if (p) navigate(p);
                        }}
                    >
                        {creation.users?.firstName || "Creator"}{" "}
                        {creation.users?.lastName || ""}
                    </p>
                    <button
                        className={`subscribe-btn ml-auto ${!creation.users?.is_subscribed
                            ? "unsubscribed"
                            : "subscribed"
                        }`} onClick={() =>
                                handleSubscribeUnsubscribe(
                                    creation.users?.user_id,
                                    !creation.users?.is_subscribed
                                )
                            }
                    >
                        {creation.users?.is_subscribed
                            ? "SUBSCRIBED"
                            : "SUBSCRIBE"}
                    </button>
                </div>
                <p className="fg-crea fs-xs fw-medium my-0">
                    {creation.users?.bio || "No bio available"}
                </p>
            </div> */}
            <div className="price-footer">
                {shouldShowPurchaseContainer && (
                <div className="purchase-container">
                    {shouldShowBuyNowButton && <button
                        disabled={disableActionButton || loading}
                        onClick={handlePaymentClick}
                        className={`btn red-${preview ? "outline-" : ""}btn btn-full aic jcc ${isOrderedBefore ? "pt-12" : ""} fw-bold ${disableActionButton || loading ? "disable" : ""}`}
                    >
                        <StripeTrustButtonInner
                            centerTitle="BUY NOW"
                            loading={loading}
                            loadingLabel="Processing…"
                            variant={preview ? "outline" : "solid"}
                        />
                    </button>}
                    {shouldShowSoldOutButton ? (
                        <button className="btn red-outline-btn btn-full d-flex fw-bold fs-regular jcc aic py-24">SOLD OUT</button>
                    ) : null}
                    {shouldShowPublishButton && (
                        <button
                            className="btn red-btn btn-full aic jcc fw-bold mb-16 pt-16"
                            onClick={handlePublish}
                            disabled={isPublishing}
                        >
                            {isPublishing ? (
                                <div className="d-flex aic jcc">
                                    <div
                                        className="spinner-border spinner-border-sm text-white me-2"
                                        role="status"
                                    >
                                        <span className="visually-hidden">Publishing...</span>
                                    </div>
                                </div>
                            ) : (
                                "Publish Creation"
                            )}
                        </button>
                    )}

                </div>
                )}
            </div>
            </div>
        </div>
    ) : error_creation && !loading_creation ? (
        <NotFound
            label="Creation Not Found"
            subtitle="This creation doesn't exist or has been removed by its creator."
        />
    ) : (
        <div className="container-creation" onClick={() => setOpenMenu(false)}>

            <div className="header-creation d-flex f-row">
                <span className="d-flex" onClick={() => handleGoBackRedirection(navigate, location.pathname)}>
                    <Icon alias={"chevron-left"} color="#0A0F1E" width={24} />
                </span>
                <div className="ml-auto d-flex aic">

                    <span className="bg-bg-grey icon-container mr-16 d-flex aic jcc" >
                        <Icon alias={"plus_v2"} stroke="#FF3232" color='none' width={20} />
                    </span>
                    <span className={`d-flex p-relative`} onClick={(e) => { e.stopPropagation(); setOpenMenu(!openMenu) }}>
                        <span className='bg-bg-grey icon-container'><Icon alias={"three_dots"} strokeWidth={2} stroke="#FF3232" width={24} /></span>

                    </span>
                </div>
            </div>

            <ViewCreationSkeleton /></div>
    );
};

export default ViewCreation;